/* eslint-disable no-debugger */

import {
  DataProvider,
  fetchUtils,
  GetListParams,
  GetOneParams,
} from 'react-admin';
import { axiosInstance } from 'services/http';
import {
  ACCOUNTING_REPORT_RESOURCE,
  downloadAccountingReport,
  PayPeriod,
} from './accountingReport';
import {
  CondOperator,
  QueryFields,
  QueryFilter,
  QueryJoin,
  QuerySort,
  RequestQueryBuilder,
} from '@nestjsx/crud-request';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import orderBy from 'lodash/orderBy';
import intersectionBy from 'lodash/intersectionBy';
import { GeoMatchStatusEnum } from '@vatos-pas/common';
import { REGION_AUDIT_WATCH_FIELDS } from 'modules/region-audit/config/auditColumns';
import { SUBDIVISION_AUDIT_WATCH_FIELDS } from 'modules/subdivision-audit/config/auditColumns';
import { filterByFieldChanges } from './auditChangeDetection';
// TODO This is a uuid hack to filter works based on a uuid
import { validate as uuidValidate } from 'uuid';
import { POFilterStatus } from 'modules/jobs/views/filters/job-sheet-filters';
import { OrderDirection } from 'components/OrderButton';
import { GEO_STATUS_NULL_SENTINEL } from 'modules/bumpout-timesheet/constants';
import { requestCanceller } from './requestCancellation';
import { trackEvent } from 'utils/mixpanel';

const httpClient = fetchUtils.fetchJson;

/**
 * Audit records (subdivision-audit, region-audit) are filtered client-side to
 * show only records where cost fields actually changed. This requires fetching
 * multiple records to compare consecutive snapshots.
 *
 * Limitations:
 * - Records beyond this limit may lose older history
 * - Pagination is disabled (all filtered results returned at once)
 * - If this becomes a bottleneck, move filtering to server-side API
 */
const AUDIT_RECORD_LIMIT = 1000;

// Presence in this map is what marks a resource as an audit list: it drives
// both the unpaginated fetch and the change-detection filtering below.
const AUDIT_WATCH_FIELDS_BY_RESOURCE: Record<string, string[]> = {
  'subdivision-audit': SUBDIVISION_AUDIT_WATCH_FIELDS,
  'region-audit': REGION_AUDIT_WATCH_FIELDS,
};

const parseNumberInput = value => {
  if (typeof value === 'string') {
    value = value.replace(',', '.');

    const parsedValue = parseFloat(value);

    return isNaN(parsedValue) ? null : parseFloat(parsedValue.toFixed(2));
  }
  return value;
};

const BOARD_COST_FIELDS_DW = [
  'dwHangingCostPerBoard',
  'dwFinishingCostPerBoard',
  'dwSprayingCostPerBoard',
];

const BOARD_COST_FIELDS_DWP = [
  'dwpHangingCostPerBoard',
  'dwpFinishCostPerBoard',
  'dwpPrimeCostPerSqft',
  'dwpBump1CostPerBoard',
  'dwpTrim1CostPerSqft',
  'dwpInteriorPaintCostPerSqft',
  'dwpBump2CostPerBoard',
  'dwpTrim2CostPerSqft',
  'dwpFinalPaintCostPerSqft',
  'dwpBump3CostPerBoard',
  'dwpCMBluetapeCostPerSqft',
  'dwpFinalBluetapeCostPerSqft',
  'interiorOptionsPercentage',
  'exteriorOptionsPercentage',
  'trimOptionPercentage',
];

const commonCostFields = [
  'repairsRate',
  'garageCostFixed',
  'pieceworkPercentage',
  'specialJobsPercentage',
  'highCeilingPerBoard',
  'screwInspectionPerBoard',
];

const getMessage = (error: any) => {
  if (
    error.response &&
    error.response.data &&
    error.response.data.message &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message;
  } else {
    return (
      (error.response &&
        error.response.data &&
        error.response.data.message &&
        error.response.data.message[0]) ||
      (error.response && error.response.statusText) ||
      error.message
    );
  }
};

/**
 * Converts flat react-admin filters ("field||$op": value) to @nestjsx/crud
 * search syntax ({ field: { $op: value } }).
 *
 * NOTE: This intentionally duplicates date/operator logic from composeQuery.
 * We avoid refactoring composeQuery itself to not risk breaking other
 * resources. Ideally both should share the same logic in the future.
 */
const flatFiltersToSearch = (
  filters: Record<string, any>,
): Record<string, unknown> => {
  const search: Record<string, unknown> = {};

  for (const [keyWithOp, value] of Object.entries(filters)) {
    const [field, operator] = keyWithOp.split('||');

    let processed: unknown = value;

    // Date range injection — same rules as composeQuery
    const dateRangeFields = ['dateApproved', 'datePaid', 'repairDate'];
    if (dateRangeFields.includes(field)) {
      if (operator === '$gte')
        processed = new Date(`${value} 00:00:00`).toISOString();
      if (operator === '$lte')
        processed = new Date(`${value} 23:59:59`).toISOString();
    }
    if (
      (field === 'currentPhaseDateRequested' ||
        field === 'jobPhaseDateRequested') &&
      operator === '$eq'
    ) {
      processed = DateTime.fromISO(`${value}T00:00:00`, {
        zone: 'America/New_York',
      }).toString();
    }

    if (operator) {
      search[field] = { ...(search[field] as object), [operator]: processed };
    } else {
      const isExact =
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        (typeof value === 'string' &&
          (value.match(/^\d+$/) || uuidValidate(value)));
      search[field] = { [isExact ? '$eq' : '$cont']: value };
    }
  }

  return search;
};

type GeoStatusFilterConfig = {
  filterKey: string;
  statusField: string;
  geoField: string;
};

/**
 * Rewrites flat react-admin filters on geo status fields into @nestjsx/crud
 * search (s=) syntax. Backend stamps MISMATCH for two distinct cases:
 * a real distance violation (geo NOT NULL) and a row that never captured
 * GPS (geo NULL). The UI exposes them as separate choices, so we split:
 *  - MISMATCH                  → "status = MISMATCH AND geo IS NOT NULL"
 *  - GEO_STATUS_NULL_SENTINEL  → "status = MISMATCH AND geo IS NULL"
 * Flat filters can't express $isnull / $notnull / $or, hence the rewrite.
 */
const applyGeoStatusFilterRewrites = (
  filters: Record<string, unknown> | undefined | null,
  configs: GeoStatusFilterConfig[],
): Record<string, unknown> => {
  const base: Record<string, unknown> = { ...(filters ?? {}) };

  const active = configs.flatMap(config => {
    const value = base[config.filterKey];
    if (!Array.isArray(value)) return [];

    const hasNoGps = value.includes(GEO_STATUS_NULL_SENTINEL);
    const hasMismatch = value.includes(GeoMatchStatusEnum.MISMATCH);
    if (!hasNoGps && !hasMismatch) return [];

    const plainValues = value.filter(
      (v: string) =>
        v !== GEO_STATUS_NULL_SENTINEL && v !== GeoMatchStatusEnum.MISMATCH,
    );
    delete base[config.filterKey];
    return [{ config, hasNoGps, hasMismatch, plainValues }];
  });

  if (active.length === 0) return base;

  const search = flatFiltersToSearch(base);

  const sentinelClauses = active.map(
    ({ config, hasNoGps, hasMismatch, plainValues }) => {
      const branches: Record<string, unknown>[] = [];

      if (plainValues.length > 0) {
        branches.push({ [config.statusField]: { $in: plainValues } });
      }

      if (hasMismatch) {
        branches.push({
          $and: [
            { [config.statusField]: { $eq: GeoMatchStatusEnum.MISMATCH } },
            { [config.geoField]: { $notnull: true } },
          ],
        });
      }

      if (hasNoGps) {
        branches.push({
          $and: [
            { [config.statusField]: { $eq: GeoMatchStatusEnum.MISMATCH } },
            { [config.geoField]: { $isnull: true } },
          ],
        });
      }

      return branches.length === 1 ? branches[0] : { $or: branches };
    },
  );

  return {
    s: {
      $and: [
        ...Object.entries(search).map(([k, v]) => ({ [k]: v })),
        ...sentinelClauses,
      ],
    },
  };
};

const composeQuery = (
  paramsFilter: any,
  additionalFilters?: QueryFilter[],
): RequestQueryBuilder => {
  const queryBuilder = RequestQueryBuilder.create();

  if (
    !paramsFilter ||
    paramsFilter === '' ||
    (typeof paramsFilter.q !== 'undefined' && paramsFilter.q === '')
  ) {
    paramsFilter = {};
  }

  if (paramsFilter.s) {
    return queryBuilder.search(paramsFilter.s);
  }

  const flatFilter = fetchUtils.flattenObject(paramsFilter);
  const queryFilters: QueryFilter[] = [];

  for (const key of Object.keys(flatFilter)) {
    // inject time zone onto date ranges (date input as filter is broken)
    if (key === 'dateApproved||$gte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 00:00:00`).toISOString();
    }
    if (key === 'dateApproved||$lte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 23:59:59`).toISOString();
    }
    if (key === 'datePaid||$gte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 00:00:00`).toISOString();
    }
    if (key === 'datePaid||$lte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 23:59:59`).toISOString();
    }
    if (key === 'repairDate||$gte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 00:00:00`).toISOString();
    }
    if (key === 'repairDate||$lte') {
      flatFilter[key] = new Date(`${flatFilter[key]} 23:59:59`).toISOString();
    }

    if (
      key === 'currentPhaseDateRequested||$eq' ||
      key === 'jobPhaseDateRequested||$eq'
    ) {
      const overrideZone = DateTime.fromISO(`${flatFilter[key]}T00:00:00`, {
        zone: 'America/New_York',
      });
      flatFilter[key] = overrideZone.toString();
    }

    const splitKey = key.split('||');

    let field = splitKey[0];

    if (field === '$join') {
      queryBuilder.setJoin(flatFilter[key]);
      continue;
    }

    if (field.startsWith('_') && field.includes('.')) {
      field = field.split(/\.(.+)/)[1];
    }

    let ops = splitKey[1];

    if (!ops) {
      if (
        typeof flatFilter[key] === 'number' ||
        typeof flatFilter[key] === 'boolean' ||
        (flatFilter[key].match && flatFilter[key].match(/^\d+$/)) ||
        uuidValidate(flatFilter[key])
      ) {
        ops = CondOperator.EQUALS;
      } else {
        ops = CondOperator.CONTAINS;
      }
    }

    queryFilters.push({
      field,
      operator: ops,
      value: flatFilter[key],
    } as QueryFilter);
  }

  if (additionalFilters) {
    queryFilters.push(...additionalFilters);
  }

  queryBuilder.setFilter(queryFilters);

  return queryBuilder;
};

const overrideExactDateFilter = (filters: any, field: string) => {
  if (!filters[field]) return;

  const date = filters[field];
  delete filters[field];
  const name = field.split('||')[0];
  filters[`${name}||$gte`] = date + 'T00:00:00Z';
  filters[`${name}||$lte`] = date + 'T23:59:59Z';
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DATE_RANGE_BOUNDARIES = {
  $gte: 'T00:00:00Z',
  $lte: 'T23:59:59Z',
};

/**
 * Pins a `||$gte` / `||$lte` date range to UTC day boundaries, the same
 * convention as overrideExactDateFilter. Values already carrying a time part
 * are left untouched so re-running on the same filter object is safe.
 */
const overrideDateRangeFilter = (filters: any, name: string) => {
  for (const [operator, boundary] of Object.entries(DATE_RANGE_BOUNDARIES)) {
    const key = `${name}||${operator}`;
    const value = filters[key];
    if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
      filters[key] = value + boundary;
    }
  }
};

const OverridableResources = {
  users: 'user',
  'board-pre-fill': 'model-board-sheet',
};

/**
 * Create endpoints answering with a summary instead of a record; react-admin
 * requires an id on every create response.
 */
const ID_LESS_CREATE_RESOURCES = [
  'board-excel-floor',
  'contractor-user/batch-process',
  'repair-payment/batch-process',
  'subdivision-supervisor/batch-process',
  'job-phase-paid/batch',
  'job-phase-paid/batch/summary',
  'bump-resource-sheet-paid-batch',
  'bump-resource-sheet-paid-batch/summary',
];

export const dataProvider: DataProvider = {
  getList: (
    resource,
    params: GetListParams & { join?: QueryJoin[]; select?: QueryFields },
  ) =>
    new Promise((resolve, reject) => {
      const start = performance.now();

      // Gerar chave única para esta requisição (resource + filtros + ordenação)
      // Não inclui paginação para permitir navegação livre entre páginas
      const requestKey = `${resource}_${JSON.stringify(
        params.filter || {},
      )}_${JSON.stringify(params.sort || {})}`;

      console.log('[DataProvider] 📤 getList called:', {
        resource,
        filter: params.filter,
        sort: params.sort,
        pagination: params.pagination,
        requestKey,
      });

      // Obter signal e cancelar TODAS as requisições anteriores do mesmo recurso
      const signal = requestCanceller.getSignal(requestKey, resource);

      const overriddenResource = OverridableResources?.[resource];
      let newResource = overriddenResource ?? resource;
      const { page, perPage } = params.pagination;

      if (resource === 'subdivision') {
        if (window.location.href.includes('#/subdivision')) {
          newResource = 'subdivision-sheet';
        }
      }

      if (resource === 'job-warranty-sheet') {
        if (params.filter && params.filter['activeJob||$eq']) {
          delete params.filter['activeJob||$eq'];
        }
        if (params.sort.field === 'sprayPhaseDateStart') {
          params.sort.field = 'bump1PhaseDateStart';
        }
      }

      if (resource === 'tn-warranty-list') {
        newResource = 'job-warranty-sheet';
        if (params.filter && params.filter['activeJob||$eq']) {
          delete params.filter['activeJob||$eq'];
        }
      }

      if (resource === 'job-sheet') {
        if (params.filter && params.filter['poStatus||$eq']) {
          if (params.filter['poStatus||$eq'] === POFilterStatus.UnSigned) {
            delete params.filter['poStatus||$eq'];
            params.filter['poStatus||$ne'] = POFilterStatus.Signed;
          }
        }
      }

      if (resource === 'job-master-sheet') {
        // Only apply Drywall filter if URL contains /job-master-sheet
        const shouldApplyDrywallFilter =
          window.location.hash.includes('/job-master-sheet');

        if (params.filter) {
          const filters = Object.entries(params.filter);

          const search = filters.reduce((acc, [keyWithOperator, value]) => {
            const [key, operator] = keyWithOperator.split('||');

            if (key === 'contractorId') {
              acc.$or = ['finishPhase', 'hangPhase', 'sprayPhase'].map(
                phase => ({ [`${phase}ContractorId`]: { $eq: value } }),
              );
            } else {
              acc[key] = { [operator]: value };
            }

            return acc;
          }, {} as Record<string, { [x: string]: unknown } | { [x: string]: unknown }[]>);

          // Add automatic filter for Drywall jobs only if on V1 route
          if (shouldApplyDrywallFilter) {
            search.phaseStructureType = { $eq: 'Drywall' };
          }

          params.filter = {
            s: search,
          };
        } else if (shouldApplyDrywallFilter) {
          // If no filters, still add the Drywall filter (only on V1 route)
          params.filter = {
            s: {
              phaseStructureType: { $eq: 'Drywall' },
            },
          };
        }
      }

      if (resource === 'job-master-sheet-v2') {
        // Redirect V2 to use the same endpoint as V1
        newResource = 'job-master-sheet';

        if (params.filter) {
          const filters = Object.entries(params.filter);

          const search = filters.reduce((acc, [keyWithOperator, value]) => {
            const [key, operator] = keyWithOperator.split('||');

            if (key === 'contractorId') {
              acc.$or = ['finishPhase', 'hangPhase', 'sprayPhase'].map(
                phase => ({ [`${phase}ContractorId`]: { $eq: value } }),
              );
            } else {
              acc[key] = { [operator]: value };
            }

            return acc;
          }, {} as Record<string, { [x: string]: unknown } | { [x: string]: unknown }[]>);

          // Add automatic filter for DrywallAndPaint jobs only
          search.phaseStructureType = { $eq: 'DrywallAndPaint' };

          params.filter = {
            s: search,
          };
        } else {
          // If no filters, still add the DrywallAndPaint filter
          params.filter = {
            s: {
              phaseStructureType: { $eq: 'DrywallAndPaint' },
            },
          };
        }
      }

      if (resource === 'material-delivery-sheet') {
        if (params.filter) {
          overrideExactDateFilter(params.filter, 'dateExpected||$eq');
          overrideExactDateFilter(params.filter, 'datePromised||$eq');
          overrideExactDateFilter(params.filter, 'dateDelivery||$eq');
          overrideDateRangeFilter(params.filter, 'orderDate');
        }
        if (!params.filter['noPromissedDate||$eq']) {
          delete params.filter['noPromissedDate||$eq'];
        }
        if (!params.filter['noDeliveryDate||$eq']) {
          delete params.filter['noDeliveryDate||$eq'];
        }
        if (!params.filter['late||$eq']) {
          delete params.filter['late||$eq'];
        }
      }

      if (resource === 'job-phase-receivable-sheet' && params.filter) {
        // "Empty Internal Invoice" is a Yes/No/All select on a synthetic key:
        // Yes -> internalInvoiceNumber IS NULL, No -> IS NOT NULL, All -> no filter.
        // A single source can't bind both operators, so translate it here.
        const emptyInvoice = params.filter.emptyInternalInvoice;
        if (emptyInvoice === true) {
          params.filter['internalInvoiceNumber||$isnull'] = true;
        } else if (emptyInvoice === false) {
          params.filter['internalInvoiceNumber||$notnull'] = true;
        }
        delete params.filter.emptyInternalInvoice;
      }

      if (resource === 'bump-resource-sheet') {
        params.filter = applyGeoStatusFilterRewrites(params.filter, [
          {
            filterKey: 'loggedGeoStatus||$in',
            statusField: 'loggedGeoStatus',
            geoField: 'loggedGeo',
          },
          {
            filterKey: 'approvedGeoStatus||$in',
            statusField: 'approvedGeoStatus',
            geoField: 'approvedGeo',
          },
        ]);
      }

      const queryBuilder = composeQuery(params.filter);

      // Audit lists page client-side after change-detection, so the request
      // can't be paginated server-side.
      if (resource in AUDIT_WATCH_FIELDS_BY_RESOURCE) {
        queryBuilder.setLimit(AUDIT_RECORD_LIMIT);
      } else {
        queryBuilder
          .setLimit(perPage)
          .setPage(page)
          .setOffset((page - 1) * perPage);
      }

      queryBuilder
        .sortBy(params.sort as QuerySort)
        .select(params.select as QueryFields);

      const query = queryBuilder.query();
      const url = `${newResource}?${query}`;

      console.log('[DataProvider] 🌐 Making axios request:', url);

      return axiosInstance
        .get(url, { signal })
        .then(response => {
          console.log('[DataProvider] ✅ Request SUCCESS for:', requestKey);

          // Limpar controller após sucesso
          requestCanceller.removeController(requestKey);

          const duration = performance.now() - start;

          trackEvent('api_call_duration', {
            resource: newResource,
            fullURL: url,
            url: window.location.pathname,
            duration: Math.round(duration),
            page: window.location.pathname,
            role: localStorage.getItem('userRoles'),
            env: process.env.REACT_APP_MIXPANEL_ENV,
          });
          if (
            resource === 'job-master-sheet' ||
            resource === 'job-master-sheet-v2' ||
            resource === 'job-archived-sheet'
          ) {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: item.jobId,
            }));
          }
          if (resource === 'job-order') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: item.spOrderId,
            }));
          }
          if (resource === 'pre-rock-sheet') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: item.jobId,
            }));
          }
          if (resource === 'job-order-board') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: item.jobId,
            }));
          }
          if (resource === 'job-order-item') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: item.jobId,
            }));
          }
          if (resource === 'contractor-allocated-capacity') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              id: `${item.contractorId}_${item.resourceType}`,
            }));
          }
          if (resource === 'contractor-user/batch-process') {
            response.data.data = response.data.data.map((item: any) => ({
              ...item,
              // react-admin validation
              id: uuidv4(),
            }));
          }

          // Audit lists show only records where a watched field changed vs the
          // chronological predecessor, so change-detection runs on a
          // createdAt-DESC copy and intersectionBy then restores the user's
          // requested sort order.
          const auditWatchFields = AUDIT_WATCH_FIELDS_BY_RESOURCE[resource];
          if (auditWatchFields) {
            const allRecords = response.data.data;

            const chronologicalRecords = orderBy(
              allRecords,
              ['createdAt'],
              ['desc'],
            );

            const filteredByChanges = filterByFieldChanges(
              chronologicalRecords,
              auditWatchFields,
            );

            // intersectionBy generic-instantiated as `any` because DataProvider's
            // `getList<RecordType>` generic cannot be satisfied by a concrete
            // record type at the implementation site (TS variance).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const finalRecords = intersectionBy<any, any>(
              allRecords,
              filteredByChanges,
              r => r.id,
            );

            return resolve({
              data: finalRecords,
              total: finalRecords.length,
            });
          }

          return resolve({
            data: response.data.data,
            total: response.data.total,
          });
        })
        .catch(error => {
          // Verificar se foi cancelamento (não é erro real)
          if (requestCanceller.isAbortError(error)) {
            // Requisição foi cancelada intencionalmente
            // Não rejeitar nem mostrar erro ao usuário
            return;
          }

          // Limpar controller após erro real
          requestCanceller.removeController(requestKey);

          const message = getMessage(error);

          error.message = message;
          return reject(error);
        });
    }),
  getOne: (
    resource,
    params: GetOneParams & {
      join?: QueryJoin[];
      filter?: { [key: string]: string };
      payPeriod?: PayPeriod;
    },
  ) => {
    const queryBuilder = composeQuery(params.filter);

    if (resource === 'route') {
      params.join = [
        {
          field: 'subdivisions',
        },
      ];
    }

    queryBuilder.setJoin(params.join as QueryJoin[]);

    const query = queryBuilder.query();

    if (resource === ACCOUNTING_REPORT_RESOURCE) {
      return downloadAccountingReport(query, params.payPeriod);
    }

    return axiosInstance
      .get(`/${resource}/${params.id}?${query}`)
      .then(response => {
        if (
          response.data.userRoles ||
          response.data.contractorRegions ||
          response.data.userRegions ||
          response.data.supplierRegions
        ) {
          delete response.data.contractorRegions;
          delete response.data.userRoles;
          delete response.data.userRegions;
          delete response.data.supplierRegions;
        }
        return {
          data: response.data,
        };
      });
  },
  getMany: (resource, params) =>
    new Promise((resolve, reject) => {
      const queryBuilder = RequestQueryBuilder.create().setFilter({
        field: 'id',
        operator: CondOperator.IN,
        value: `${params.ids}`,
      });

      const url = `${resource}?${queryBuilder.query()}`;

      return axiosInstance
        .get(url)
        .then(response =>
          resolve({
            data: response.data.data,
          }),
        )
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        });
    }),
  getManyReference: (resource, params) => {
    return new Promise((resolve, reject) => {
      if (
        (resource === 'user-role' ||
          resource === 'user-region' ||
          resource === 'contractor-region' ||
          resource === 'supplier-region') &&
        !params.id
      ) {
        return resolve({
          data: [],
          total: 0,
        });
      }
      const { page, perPage } = params.pagination;

      const additionalFilters: QueryFilter[] = [];

      if (params.id && params.target) {
        additionalFilters.push({
          field: params.target,
          operator: CondOperator.EQUALS,
          value: params.id,
        });
      }

      const queryBuilder = composeQuery(params.filter, additionalFilters);

      queryBuilder
        .sortBy(params.sort as QuerySort)
        .setLimit(perPage)
        .setOffset((page - 1) * perPage);

      const url = `${resource}?${queryBuilder.query()}`;
      return axiosInstance
        .get(url)
        .then(({ data }) =>
          resolve({
            data: data.data,
            total: 15,
          }),
        )
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        });
    });
  },
  update: (resource, params) => {
    const overriddenResource = OverridableResources?.[resource];
    const newResource = overriddenResource ?? resource;
    let boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DW];
    if (params.data.phaseStructureType === 'DrywallAndPaint') {
      boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DWP];
      BOARD_COST_FIELDS_DW.forEach(field => {
        params.data[field] = null;
      });
    }

    if (params.data.phaseStructureType === 'Drywall') {
      boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DW];
      BOARD_COST_FIELDS_DWP.forEach(field => {
        params.data[field] = null;
      });
    }

    const boardCostsNoRepairRate = boardCostFields.slice(1);

    if (resource === 'region' || resource === 'subdivision') {
      // Parse each board cost field to ensure it is a valid number
      boardCostFields.forEach(field => {
        params.data[field] = parseNumberInput(params.data[field]);
      });
    }

    if (resource === 'route') {
      delete params.data?.supervisorUser;
    }

    if (resource === 'subdivision') {
      const newRouteId = params.data?.routeId;
      delete params.data?.route;

      params.data.route = {
        id: newRouteId,
      };

      // Nullify costs if override is not set in subdivision
      if (!params.data.overrideRegionCostPerBoard) {
        // Skip repairsRate
        boardCostsNoRepairRate.forEach(field => {
          params.data[field] = null;
        });
      }
    }

    return new Promise((resolve, reject) =>
      axiosInstance
        .patch(`${newResource}/${params.id}`, params.data)
        .then(response =>
          resolve({
            data: response.data,
          }),
        )
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        }),
    );
  },
  put: (resource, params) => {
    const overriddenResource = OverridableResources?.[resource];
    const newResource = overriddenResource ?? resource;

    return new Promise((resolve, reject) =>
      axiosInstance
        .put(`${newResource}/${params.id}`, params.data)
        .then(response =>
          resolve({
            data: response.data,
          }),
        )
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        }),
    );
  },
  updateMany: (resource, params) => {
    const overriddenResource = OverridableResources?.[resource];
    const newResource = overriddenResource ?? resource;

    return httpClient(`${newResource}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },
  create: (resource, params) => {
    const overriddenResource = OverridableResources?.[resource];
    const newResource = overriddenResource ?? resource;

    let boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DW];
    if (params.data.phaseStructureType === 'DrywallAndPaint') {
      boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DWP];
      BOARD_COST_FIELDS_DW.forEach(field => {
        params.data[field] = null;
      });
    }
    if (params.data.phaseStructureType === 'Drywall') {
      boardCostFields = [...commonCostFields, ...BOARD_COST_FIELDS_DW];
      BOARD_COST_FIELDS_DWP.forEach(field => {
        params.data[field] = null;
      });
    }
    const boardCostsNoRepairRate = boardCostFields.slice(1);

    if (resource === 'region' || resource === 'subdivision') {
      // Set any non-numeric board cost fields to null
      boardCostFields.forEach(field => {
        if (typeof params.data[field] !== 'number') {
          params.data[field] = null;
        }
      });
    }

    if (resource === 'subdivision' && !params.data.overrideRegionCostPerBoard) {
      boardCostsNoRepairRate.forEach(field => {
        params.data[field] = null;
      });
    }

    return new Promise((resolve, reject) =>
      axiosInstance
        .post(`${newResource}`, params.data)
        .then(response => {
          if (ID_LESS_CREATE_RESOURCES.includes(newResource)) {
            response.data = { ...response.data, id: uuidv4() };
          }

          return resolve({
            data: response.data,
          });
        })
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        }),
    );
  },
  delete: (resource, params) =>
    new Promise((resolve, reject) =>
      axiosInstance
        .delete(`${resource}/${params.id}`)
        .then(({ data }) => resolve({ data }))
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        }),
    ),
  deleteMany: (resource, params) => {
    return new Promise((resolve, reject) => {
      return Promise.all(
        params.ids.map(id => axiosInstance.delete(`${resource}/${id}`)),
      )
        .then(responses =>
          resolve({ data: responses.map(response => response.data) }),
        )
        .catch(error => {
          const message = getMessage(error);

          error.message = message;
          return reject(error);
        });
    });
  },
  move: (
    resource: string,
    params: { id: number | string; direction: OrderDirection },
  ) =>
    new Promise((resolve, reject) =>
      axiosInstance
        .patch(
          `${resource}/${params.id}/move`,
          JSON.stringify({ direction: params.direction }),
        )
        .then(response => resolve({ data: response }))
        .catch(error => {
          const message = getMessage(error);
          error.message = message;
          return reject(error);
        }),
    ),
};

export default dataProvider;
